import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase/client";
import { authKeys } from "@/hooks/useAuth";

export function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setBusy(false);
    if (signInError) {
      setError(signInError.message);
      return;
    }
    queryClient.setQueryData(authKeys.session(), data.session);
    navigate("/app/today", { replace: true });
  }

  async function handleGoogle() {
    setBusy(true);
    setError(null);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/app/today`,
      },
    });
    setBusy(false);
    if (oauthError) setError(oauthError.message);
  }

  return (
    <main
      className="flex min-h-screen flex-col items-center justify-center px-6"
      style={{ background: "#FFFCF8", color: "#1A1620" }}
    >
      <div className="w-full max-w-sm">
        <h1 className="font-display text-3xl" style={{ color: "#8A4E9C" }}>
          Welcome back
        </h1>
        <p className="mt-1 text-sm" style={{ color: "#9A8A78" }}>
          Sign in to keep planning your garden.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <label className="block">
            <span className="text-sm font-medium">Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg px-3 py-2 text-base"
              style={{ border: "1px solid #EAE4DA", background: "#FFFFFF" }}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg px-3 py-2 text-base"
              style={{ border: "1px solid #EAE4DA", background: "#FFFFFF" }}
            />
          </label>

          {error ? (
            <p
              role="alert"
              className="rounded-md px-3 py-2 text-sm"
              style={{ background: "#FBEDE6", color: "#A05A38" }}
            >
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-full px-5 py-3 text-base font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: "#8A4E9C", color: "#FFFFFF" }}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs" style={{ color: "#9A8A78" }}>
          <span className="h-px flex-1" style={{ background: "#EAE4DA" }} />
          or
          <span className="h-px flex-1" style={{ background: "#EAE4DA" }} />
        </div>

        <button
          type="button"
          onClick={handleGoogle}
          disabled={busy}
          className="w-full rounded-full px-5 py-3 text-base font-medium transition-colors disabled:opacity-60"
          style={{ border: "1px solid #EAE4DA", background: "#FFFFFF", color: "#1A1620" }}
        >
          Continue with Google
        </button>

        <p className="mt-8 text-center text-sm" style={{ color: "#9A8A78" }}>
          New here?{" "}
          <Link to="/signup" className="font-medium" style={{ color: "#8A4E9C" }}>
            Create an account
          </Link>
        </p>
      </div>
    </main>
  );
}
